// src/features/leases/leaseTabConfig.js
//
// LSE-TAB-REDESIGN — SSOT for the redesigned Lease tab's static
// configuration (panel labels, per-panel field lists for the gap-fill
// counter, currency-toggle storage key shape).
//
// Per L52: any constants the redesign introduces live here, not
// scattered across components. AccordionFieldPanel reads its field
// list from here; the gap-fill counter reads the same list so the
// "X of N filled" count matches what the panel actually renders.
//
// Adding a new field to an accordion panel is a one-line edit here
// plus the panel's rendering loop — no separate counter list to keep
// in sync.

// ─── Urgency thresholds — shared with LeaseTimeline + DaysRemainingPill ───
//
// Both the timeline's progress-bar color and the days-remaining pill derive
// their "red" threshold from these constants so they stay in lockstep.
// Changing RED_THRESHOLD_DAYS here affects both surfaces simultaneously.
//
// INC-01 (LSE-STYLE-NORMALIZE-1): URGENT_RED and URGENT_AMBER now route
// through T so leaseTabConfig is no longer a second source of truth for
// these values. Callers that import from here continue to work unchanged.
import { T } from "../../app/tokens.js";
export const RED_THRESHOLD_DAYS = 90;
export const URGENT_RED   = T.urgentRed;  // #A32D2D — via T (INC-01)
export const URGENT_AMBER = T.amber;      // #f59e0b — via T (was local literal)

// ─── Feature flags ────────────────────────────────────────────────────────────
//
// These are re-exported from the centralized featureFlags.js SSOT
// (src/config/featureFlags.js). Existing imports of these names from
// leaseTabConfig.js continue to work unchanged.
export { FEATURE_LOCATION_COST_TAB, FEATURE_LEASE_INTELLIGENCE } from "../../config/featureFlags.js";

// Urgency colors by anchor mode. Contractual = red (real lease cliff).
// Projected = amber (rolling notice window, not a cliff).
// Ongoing = null (no urgency color; open-ended leases have no right anchor).
export function urgencyColorForMode(mode) {
  if (mode === "contractual")                                    return URGENT_RED;
  if (mode === "projected_expiration" || mode === "projected_notice") return URGENT_AMBER;
  return null;
}

export const ACCORDION_PANELS = [
  {
    key:   "options_rights",
    label: "Options & Rights",
    fields: [
      "renewalCount",
      // noticePeriodText excluded: rendered twice in this panel but both
      // instances are canWrite=false (derived / complex display with deadline
      // pill). Including it in the gap-fill scan would cause the CTA to
      // expand on a field the user can never edit — dead-end UX (architect
      // review finding, 2026-05-21). It still renders in the panel body.
      "breakDate",
      "rofrRofoNotes",
      "expansionOptionNotes",
      // LEASE-FIELDS-ADD-DROP: holdoverPercent removed (column dropped in 0216).
      // rentPaymentType and termType removed from accordion — both now promoted
      // to main card rows (FinancialTermsCard and LeaseSummaryCard respectively).
    ],
  },
  {
    key:   "operating_expenses",
    label: "Operating Expenses",
    fields: [
      "opexStructure",
      "baseYear",
      "baseYearOpex",
      "baseYearTax",
      "opexCapPercent",
      "securityDepositAmount",
      "noticeAddress",
    ],
  },
  {
    key:   "restrictions_restoration",
    label: "Restrictions & Restoration",
    fields: [
      "permittedUse",
      "subletConsentRequired",
      "sqftMeasurementStandard",
      "subletRecaptureRight",
      "restorationObligations",
      "subletProfitSharePercent",
    ],
  },
];

// ─── Key terms field registry ─────────────────────────────────────────────────
// Canonical field set for the "Key terms" group on the Terms tab.
// Used for count pill computation ("N of M") and "Not in this lease" footer.
// Fields with always-present values (type, startDate, endDate, id, termLengthMonths)
// are included so the total reflects all trackable key term attributes.
// areas / servicedOfficeProvider are type-conditional; excluded from the
// canonical count (they appear in the rendered rows when present but do not
// inflate the "missing" list for leases that don't use them).
export const KEY_TERMS_FIELDS = [
  { field: "type",             label: "Type" },
  { field: "termType",         label: "Term type" },
  { field: "size",             label: "Size" },
  { field: "startDate",        label: "Commencement" },
  { field: "possessionDate",   label: "Possession" },
  { field: "endDate",          label: "End date" },
  { field: "termLengthMonths", label: "Term length" },  // value from computed prop
];

// FEATURE_LEASE_INTELLIGENCE is now in src/config/featureFlags.js (re-exported above).

// currencyTogglePrefKey — removed (ITEM 10, 2026-05-21).
// Currency preference is no longer persisted to localStorage;
// the toggle resets to USD on every page load. The function had
// no callers after that change, so it is deleted for cleanliness
// (architect direction: "delete for cleanliness" path chosen).

// ─── Canonical date parser ────────────────────────────────────────────────────
//
// SSOT for all date-only parsing across the leases feature.
// Extracts the YYYY-MM-DD portion from any ISO string (date-only OR ISO
// datetime) and constructs a Date at LOCAL midnight, preventing the
// timezone off-by-one that new Date("2026-09-30T00:00:00.000Z") causes
// for users west of UTC (UTC midnight → previous calendar day locally).
//
// Regex matches without $ anchor so "2026-09-30T00:00:00.000Z" works as
// well as bare "2026-09-30". Returns null for null / empty / non-matching
// input — no new Date(d) fallback that could re-introduce the UTC bug.
export function parseDateOnly(d) {
  if (d == null || d === "") return null;
  // Date objects (e.g. the `today` prop in LeaseTimeline) are returned
  // directly — no string conversion needed, no UTC risk.
  if (d instanceof Date) return Number.isFinite(d.getTime()) ? d : null;
  const s = typeof d === "string" ? d : String(d);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

// ─── LSE-TIMELINE-SENTINEL-FALLBACK ─────────────────────────────────────────
//
// Sentinel value for "no contractual end date".
// Any lease whose end_date equals this string has no fixed term — it is a
// rolling / serviced-office lease whose earliest exit is captured in
// soonest_possible_expiration and next_notice_deadline instead.
export const END_DATE_SENTINEL = "9999-12-31";

// Returns true when a value represents "no contractual end" —
// i.e. NULL, undefined, empty string, or the 9999-12-31 sentinel.
//
// Predicate operates on raw API strings. Postgres date columns serialize as
// ISO datetime (9999-12-31T00:00:00.000Z) via JSON.stringify; date-only forms
// also exist in test fixtures. startsWith covers both without requiring a
// Date parse.
export const isSentinelEndDate = (d) =>
  d === null
  || d === undefined
  || d === ""
  || d === END_DATE_SENTINEL
  || (typeof d === "string" && d.startsWith(END_DATE_SENTINEL));

// Ordered hierarchy for resolving the timeline's right anchor.
// Each tier names the source field and the rendering mode used by
// LeaseTimeline to pick a label / pill style.
export const TIMELINE_END_HIERARCHY = [
  { source: "end_date",                    mode: "contractual" },
  { source: "soonest_possible_expiration", mode: "projected_expiration" },
  { source: "next_notice_deadline",        mode: "projected_notice" },
  // Tier 4 (ongoing) is handled in resolveTimelineEndAnchor — no entry here.
];

// resolveTimelineEndAnchor
// Returns { date: ISO | null, source: string | null, mode: string }
// describing which date to use as the right anchor of the lease timeline bar.
//
// Tier 1 — contractual end_date (skipped when sentinel / null).
// Tier 2 — soonest_possible_expiration (rolling-lease projected exit).
// Tier 3 — next_notice_deadline (latest anchor if SPE also absent).
// Tier 4 — ongoing (no projectable anchor; caller renders an open-ended bar).
export function resolveTimelineEndAnchor(lease) {
  if (!isSentinelEndDate(lease.endDate)) {
    return { date: lease.endDate, source: "end_date", mode: "contractual" };
  }
  if (lease.soonestPossibleExpiration) {
    return {
      date:   lease.soonestPossibleExpiration,
      source: "soonest_possible_expiration",
      mode:   "projected_expiration",
    };
  }
  if (lease.nextNoticeDeadline) {
    return {
      date:   lease.nextNoticeDeadline,
      source: "next_notice_deadline",
      mode:   "projected_notice",
    };
  }
  return { date: null, source: null, mode: "ongoing" };
}

// ─── LSE-LEASES-LIST-URGENCY-COLUMN ──────────────────────────────────────────
//
// 5-bucket urgency classifier for the Leases List view URGENCY column.
// Mirrors the Timeline view's Gantt-bar color legend:
//   Active   (>2 yr)  — green     sortOrder 3
//   Upcoming (1–2 yr) — amber     sortOrder 2
//   Urgent   (<1 yr)  — red       sortOrder 1
//   Rolling  (sentinel / no fixed end) — empty circle  sortOrder 4
//   Expired  (≤0 d / status=expired)   — gray          sortOrder 0
//
// Precedence (evaluated top-to-bottom):
//   1. lease.status === "expired"                     → Expired
//   2. isSentinelEndDate(lease.endDate)               → Rolling
//      (no fixed term — catches all Serviced Office rolling leases and any other
//      sentinel end_date lease; fixed-term Serviced Office falls through to #3)
//   3. anchor.date is null (safety net)               → Rolling
//   4. Time-based thresholds on daysRemaining
//
// Returns:
//   { label, color, bucket, sortOrder }
//   color is null for Rolling (rendered as empty circle by UrgencyCell).
//   sortOrder: 0=Expired, 1=Urgent, 2=Upcoming, 3=Active, 4=Rolling
//   (ascending sort = most urgent first; Rolling last — not pressing)
//
export const URGENCY_DAYS_ACTIVE   = 365 * 2; // >2 yr
export const URGENCY_DAYS_UPCOMING = 365;     // 1–2 yr

// Single source-of-truth for all urgency-bucket display labels.
// leaseUrgencyBucket (below) and TERM_REMAINING_BUCKETS in leasePortfolioMetrics
// both import from here so a rename stays in sync. (task-93, task-112)
export const ROLLING_LABEL   = "Rolling";
// VOCAB-CONSISTENCY-S1: renamed from "Active" to "Secure" for the 24mo+
// remaining bucket, to avoid colliding with the unrelated "Active" labels
// used for market status, candidate pick, and lease status elsewhere in the
// app (those are context-distinct and intentionally untouched).
export const ACTIVE_LABEL    = "Secure";
export const UPCOMING_LABEL  = "Upcoming";
export const URGENT_LABEL    = "Urgent";
export const DRAFT_LABEL     = "Draft";
export const EXPIRED_LABEL   = "Expired";
// LEASES-ACTIVE-OFFICE-EXPIRED-OUTLOOK-P1: In-Use leases past their anchor
// date with no future dates are in holdover — neither Unknown nor Expired.
export const HOLDOVER_LABEL  = "Holdover";

export function leaseUrgencyBucket(lease) {
  // Precedence 0: draft lease — no term yet; show light-blue dot.
  if (lease.status === "draft") {
    return { label: DRAFT_LABEL, color: T.buildBlue, bucket: "draft", sortOrder: 5, tooltip: DRAFT_LABEL };
  }

  // Precedence 1: lifecycle status marks this lease as expired.
  // LEASES-ACTIVE-OFFICE-EXPIRED-OUTLOOK-P1: For non-vacated (In-Use) leases,
  // do not unconditionally return Expired. Instead:
  //   • If a future anchor exists (non-sentinel end_date in the future, or future
  //     SPE/NND) → fall through to the time-based logic below.
  //   • If no future anchor exists → Holdover (not Unknown, not Expired).
  // Vacated leases retain the Expired label regardless of their anchor.
  if (lease.status === "expired") {
    const isVacated = lease.locationStage === "vacated" || lease.leaseStatus === "vacant_held";
    if (isVacated) {
      return { label: EXPIRED_LABEL, color: T.defaultDot, bucket: "expired", sortOrder: 0, tooltip: EXPIRED_LABEL };
    }
    // Non-vacated (In-Use) expired lease: check for a future anchor.
    const expiredAnchor = resolveTimelineEndAnchor(lease);
    const expiredToday = new Date();
    expiredToday.setHours(0, 0, 0, 0);
    const expiredAnchorDate = expiredAnchor.date ? parseDateOnly(expiredAnchor.date) : null;
    if (expiredAnchorDate && expiredAnchorDate > expiredToday) {
      // Future anchor exists — fall through to time-based logic (don't return here).
    } else {
      // No future anchor — this is a holdover situation.
      return { label: HOLDOVER_LABEL, color: T.ragAmber, bucket: "holdover", sortOrder: 0, tooltip: HOLDOVER_LABEL };
    }
  }

  // Precedence 2: sentinel end_date → Rolling, regardless of SPE/NND.
  // All 9999-12-31 leases are open-ended by definition. SPE and NND describe
  // their earliest exit window but do not create a contractual cliff — showing
  // Upcoming (amber) for these leases was misleading.
  // (LSE-ROLLING-VISUAL-AND-DETECTION-CORRECTION)
  if (isSentinelEndDate(lease.endDate)) {
    return { label: ROLLING_LABEL, color: null, bucket: "rolling", sortOrder: 4, tooltip: ROLLING_LABEL };
  }

  // resolveTimelineEndAnchor is only needed for the time-based path below.
  const anchor = resolveTimelineEndAnchor(lease);

  // Precedence 3: no projectable date at all → Rolling (safety-net).
  if (!anchor.date) {
    return { label: ROLLING_LABEL, color: null, bucket: "rolling", sortOrder: 4, tooltip: ROLLING_LABEL };
  }

  const endDate = parseDateOnly(anchor.date);
  if (!endDate) {
    return { label: ROLLING_LABEL, color: null, bucket: "rolling", sortOrder: 4, tooltip: ROLLING_LABEL };
  }

  // Precedence 3: time-based buckets.
  // Colors match Locations HEALTH exactly: T.green / T.amber / T.red.
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const daysRemaining = Math.round((endDate - today) / 86_400_000);

  if (daysRemaining <= 0) {
    return { label: EXPIRED_LABEL,  color: T.defaultDot, bucket: "expired",  sortOrder: 0, tooltip: EXPIRED_LABEL };
  }

  // Projected (rolling notice) leases are never URGENT — they are in that
  // window by design, mirroring the isProjected demotion in termRemainingBucket.
  const isProjected = anchor.mode === "projected_expiration"
                   || anchor.mode === "projected_notice";

  if (daysRemaining < URGENCY_DAYS_UPCOMING && !isProjected) {
    return { label: URGENT_LABEL,   color: T.ragRed,    bucket: "urgent",   sortOrder: 1, tooltip: `${URGENT_LABEL} (<12mo)` };
  }
  if (daysRemaining < URGENCY_DAYS_ACTIVE) {
    // For projected-expiration leases the anchor is the soonestPossibleExpiration
    // date, so surface that date in the tooltip so users can see which exit
    // window is driving the "Upcoming" flag without opening the detail panel.
    let upcomingTooltip = `${UPCOMING_LABEL} (12-24mo)`;
    if (anchor.mode === "projected_expiration") {
      const formatted = endDate.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric" });
      upcomingTooltip = `${UPCOMING_LABEL} \u2014 earliest exit ${formatted}`;
    } else if (anchor.mode === "projected_notice") {
      const formatted = endDate.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric" });
      upcomingTooltip = `${UPCOMING_LABEL} \u2014 next notice deadline ${formatted}`;
    }
    return { label: UPCOMING_LABEL, color: T.ragAmber,  bucket: "upcoming", sortOrder: 2, tooltip: upcomingTooltip };
  }
  return { label: ACTIVE_LABEL, color: T.greenDeep, bucket: "active", sortOrder: 3, tooltip: `${ACTIVE_LABEL} (24mo+)` };
}
